import { useEffect } from "react";
import { create } from "zustand";
import { V2_ENDPOINT } from "../consts";
import { authenticatedFetch } from "../lib/authenticatedFetch";
import { fetchAllPages } from "../lib/fetchAllPages";
import type {
  Block,
  BlockCategory,
  BlockInput,
  BlockOutput,
  BlockVisibility,
  CreateBlockRequest,
  UpdateBlockRequest,
} from "../lib/api/blocks";
import type { PaginatedData } from "../types/pagination";
import type { WorkflowDefinition, WorkflowNodeConnector, WorkflowUIMetadata } from "./workflowsStore";

// ---------------------------------------------------------------------------
// API response shape
// ---------------------------------------------------------------------------

interface BlocksApiResponse {
  code: number;
  message: string;
  data: PaginatedData<Block>;
  requestId: string;
}

interface BlockApiResponse {
  code: number;
  message: string;
  data: Block;
  requestId: string;
}

interface PublishBlockApiResponse {
  code: number;
  message: string;
  data: { block: Block };
  requestId: string;
}

// ---------------------------------------------------------------------------
// Store interface
// ---------------------------------------------------------------------------

interface BlocksStore {
  blocks: Block[];
  loading: boolean;
  error: Error | null;
  lastLoadedAt: number | null;
  currentCategory: BlockCategory;
  _initialized: boolean;

  // Fetch
  loadBlocks: () => Promise<void>;
  loadBlocksByCategory: (category: BlockCategory) => Promise<void>;
  refetch: () => Promise<Block[]>;
  reset: () => void;
  getBlockById: (id: string) => Block | undefined;

  // Mutations
  createBlock: (request: CreateBlockRequest) => Promise<Block>;
  updateBlock: (id: string, request: UpdateBlockRequest) => Promise<Block>;
  publishBlock: (id: string) => Promise<Block>;
  archiveBlock: (id: string) => Promise<void>;
  forkBlock: (id: string) => Promise<Block>;

  // Internal
  _ensureInitialized: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fetchBlocks = async (category: BlockCategory = "org"): Promise<Block[]> => {
  return fetchAllPages<Block>(async (limit, offset) => {
    const params = new URLSearchParams({
      category,
      limit: String(limit),
      offset: String(offset),
    });

    const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/blocks?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch blocks: ${response.statusText}`);
    }

    const data: BlocksApiResponse = await response.json();
    if (data.code !== 0) {
      throw new Error(`API error: ${data.message}`);
    }

    return { items: data.data.items, hasMore: data.data.hasMore };
  });
};

// Dedup: prevent concurrent loads from running parallel fetch loops.
let _loadBlocksPromise: Promise<void> | null = null;

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useBlocksStore = create<BlocksStore>((set, get) => ({
  blocks: [],
  loading: false,
  error: null,
  lastLoadedAt: null,
  currentCategory: "org",
  _initialized: false,

  _ensureInitialized: () => {
    const { blocks, loading, _initialized } = get();
    if (!_initialized && blocks.length === 0 && !loading) {
      get().loadBlocksByCategory("all").catch(console.error);
    }
  },

  loadBlocks: async () => {
    return get().loadBlocksByCategory(get().currentCategory);
  },

  loadBlocksByCategory: async (category: BlockCategory) => {
    // Return existing promise if already loading
    if (_loadBlocksPromise) return _loadBlocksPromise;

    _loadBlocksPromise = (async () => {
      set({ loading: true, error: null, currentCategory: category, _initialized: true });
      try {
        const blocks = await fetchBlocks(category);
        set({ blocks, loading: false, lastLoadedAt: Date.now() });
      } catch (e) {
        const error = e instanceof Error ? e : new Error("Failed to load blocks");
        set({ error, loading: false });
        throw error;
      } finally {
        _loadBlocksPromise = null;
      }
    })();

    return _loadBlocksPromise;
  },

  refetch: async () => {
    const { currentCategory } = get();
    const blocks = await fetchBlocks(currentCategory);
    set({ blocks, lastLoadedAt: Date.now() });
    return blocks;
  },

  reset: () => {
    set({
      blocks: [],
      loading: false,
      error: null,
      lastLoadedAt: null,
      _initialized: false,
    });
  },

  getBlockById: (id: string) => {
    return get().blocks.find(b => b.id === id);
  },

  createBlock: async (request: CreateBlockRequest) => {
    const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/blocks`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`Failed to create block: ${response.statusText}`);
    }

    const data: BlockApiResponse = await response.json();
    if (data.code !== 0) {
      throw new Error(`API error: ${data.message}`);
    }

    const newBlock = data.data;
    set(state => ({
      blocks: [newBlock, ...state.blocks],
    }));

    return newBlock;
  },

  updateBlock: async (id: string, request: UpdateBlockRequest) => {
    const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/blocks/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`Failed to update block: ${response.statusText}`);
    }

    const data: BlockApiResponse = await response.json();
    if (data.code !== 0) {
      throw new Error(`API error: ${data.message}`);
    }

    const updatedBlock = data.data;
    set(state => ({
      blocks: state.blocks.map(b => (b.id === id ? updatedBlock : b)),
    }));

    return updatedBlock;
  },

  publishBlock: async (id: string) => {
    const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/blocks/${id}/publish`, {
      method: "POST",
    });

    if (!response.ok) {
      throw new Error(`Failed to publish block: ${response.statusText}`);
    }

    const data: PublishBlockApiResponse = await response.json();
    if (data.code !== 0) {
      throw new Error(`API error: ${data.message}`);
    }

    const publishedBlock = data.data.block;
    set(state => ({
      blocks: state.blocks.map(b => (b.id === id && b.version === publishedBlock.version ? publishedBlock : b)),
    }));

    return publishedBlock;
  },

  archiveBlock: async (id: string) => {
    const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/blocks/${id}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error(`Failed to archive block: ${response.statusText}`);
    }

    set(state => ({
      blocks: state.blocks.filter(b => b.id !== id),
    }));
  },

  forkBlock: async (id: string) => {
    const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/blocks/${id}/fork`, {
      method: "POST",
    });

    if (!response.ok) {
      throw new Error(`Failed to fork block: ${response.statusText}`);
    }

    const data: BlockApiResponse = await response.json();
    if (data.code !== 0) {
      throw new Error(`API error: ${data.message}`);
    }

    const forkedBlock = data.data;
    set(state => ({
      blocks: [forkedBlock, ...state.blocks],
    }));

    return forkedBlock;
  },
}));

// ---------------------------------------------------------------------------
// Convenience hook — auto-initializes on mount
// ---------------------------------------------------------------------------

export const useBlocks = () => {
  const blocks = useBlocksStore(state => state.blocks);
  const ensureInitialized = useBlocksStore(state => state._ensureInitialized);

  useEffect(() => {
    ensureInitialized();
  }, [ensureInitialized]);

  return blocks;
};

// Re-export types from API client for convenience
export type { Block, BlockInput, BlockOutput, BlockCategory, BlockVisibility, CreateBlockRequest, UpdateBlockRequest };
